import React from 'react';
import PropTypes from 'prop-types';
import JobModule from 'modules/job';
import MobileHeader from 'pages/mobileHeader';
import { connect } from 'react-redux';
import ReduxActions from 'reduxActions';
import { Components, Utils, translate } from 'versafleet-core';
import { isUserReadOnly } from 'utils/accessHelper';
import FilterPage from '../filterPage';

const { Router, FullScreenLoading, Helper } = Utils;

const SORT_OPTIONS = [{
  value: 'id',
  text: 'Created at',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}, {
  value: 'updated_at',
  text: 'Last updated',
  ascendingText: 'oldest first',
  descendingText: 'newest first',
}];

class ArchivedJobPage extends React.Component {
  constructor(props) {
    super(props);
    const filter = this._getDefaultFilter();
    this.state = {
      filter,
      showFilterPage: false,
    };
  }

  UNSAFE_componentWillMount() {
    this._loadList();
    this.props.dispatch(ReduxActions.constant.fetchList());
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.filter !== this.state.filter) {
      this._loadList();
    }
  }

  _loadList() {
    FullScreenLoading.FullScreenLoadingManager.open(this.props.strings.Job.page.loadingItems);
    this.props.dispatch(ReduxActions.job.fetchArchivedList(this.state.filter))
      .then(() => {
        FullScreenLoading.FullScreenLoadingManager.close();
      });
  }

  _getDefaultFilter() {
    return {
      date: this.props.filter.date,
      keyword: '',
      state: this._getJobStatusOptions().map(option => option.value),
      page: 1,
      per_page: 20,
      sort_by: SORT_OPTIONS[0].value,
      order_by: 'desc',
      archived: true,
      job_type: ['pickup', 'delivery', 'p2p'],
      tags: [],
    };
  }

  _onFilterChange(fieldName, value) {
    // For iOS, try to block the input date from changing the date to none
    if (fieldName === 'date' && (
      Helper.isNullOrUndefined(value) || !Helper.isNonEmptyString(value)
    )) {
      return;
    }

    const newFilter = Helper.setFilter(this.state.filter, fieldName, value);
    this._updateFilters(newFilter);
  }

  _updateFilters(filter) {
    // Check if any of the keys exist in
    // the filter store. If so, update the filter store to the filter changed
    // here
    const myJobsFilter = this.props.filter;
    Object.keys(myJobsFilter).forEach((key) => {
      if (typeof filter[key] !== 'undefined') {
        myJobsFilter[key] = filter[key];
      }
    });

    this.props.dispatch(ReduxActions.filter.updateMyJobsFilter(myJobsFilter));

    this.setState({ filter });
  }

  _getJobStatusOptions() {
    const strings = this.props.strings.Job.status;
    return [{
      text: strings.draft,
      value: 'draft',
    }, {
      text: strings.in_progress,
      value: 'in_progress',
    }, {
      text: strings.completed,
      value: 'completed',
    }, {
      text: strings.submitted,
      value: 'submitted',
    }, {
      text: strings.rejected,
      value: 'rejected',
    }];
  }

  _renderActionPanel() {
    return (
      <Components.Mobile.ActionPanel className="vf-mobile-job-page__action-panel">
        <div className="vf-mobile-job-page__action-panel__content">
          <Components.Paginator
            className="vf-mobile-job-page__action-panel__content__paginator"
            currentPage={this.props.meta.page}
            id="vf-mobile-archive-job-page__paginator"
            isCountCustomizable
            itemsPerPage={this.props.meta.per_page}
            model="jobs"
            onChange={(key, val) => this._onFilterChange(key, val)}
            totalItems={this.props.meta.total}
          />
          <Components.Input.DatePicker
            id="vf-mobile-archive-job-page__datepicker"
            onChange={value => this._onFilterChange('date', value)}
            value={this.state.filter.date}
          />
        </div>
        {!isUserReadOnly() ? (
          <Components.Button
            iconClassName="versa-add"
            id="vf-mobile-archive-job-page__new-job-button"
            onClick={() => { Router.transitionTo('/jobs/new'); }}
            showShadow={false}
          />
        ) : false}
      </Components.Mobile.ActionPanel>
    );
  }

  _renderFilterPage() {
    return this.state.showFilterPage ? (
      <FilterPage
        filter={this.state.filter}
        id="vf-mobile-archive-job-page__filter-page"
        onChange={(filter) => {
          this.setState({ showFilterPage: false });
          this._updateFilters(filter);
        }}
        onClose={() => this.setState({ showFilterPage: false })}
        sortOptions={SORT_OPTIONS}
        statusOptions={this._getJobStatusOptions()}
        tagList={this.props.jobTags || []}
        title="Filter & Sort Jobs"
      />
    ) : null;
  }

  render() {
    return (
      <div className="vf-mobile-job-page">
        <MobileHeader
          id="vf-mobile-archive-job-page__mobile-header"
          onFilterClick={() => this.setState({ showFilterPage: true })}
          onTagClick={() => Router.transitionTo('/archives/tasks')}
          tag="Jobs"
          title="Archives"
        />
        <div>
          {this._renderActionPanel()}
        </div>
        {this.props.jobs.map(job => (
          <JobModule.MobileLineItem
            data={job}
            id="vf-mobile-archive-job-page__line-item"
            onAction={() => {}}
          />
        ))}
        {this.state.showFilterPage ? this._renderFilterPage() : false}
        {this.props.children}
      </div>
    );
  }
}

ArchivedJobPage.propTypes = {
  children: PropTypes.node,
  dispatch: PropTypes.func.isRequired,
  filter: PropTypes.shape({
    date: PropTypes.string,
  }).isRequired,
  jobTags: PropTypes.arrayOf(PropTypes.string),
  jobs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  meta: PropTypes.shape({
    page: PropTypes.number,
    per_page: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  strings: PropTypes.shape({
    Job: PropTypes.shape({
      page: PropTypes.shape({
        loadingItems: PropTypes.string,
      }),
      status: PropTypes.shape({
        draft: PropTypes.string,
        in_progress: PropTypes.string,
        completed: PropTypes.string,
        submitted: PropTypes.string,
        rejected: PropTypes.string,
      }),
    }),
  }).isRequired,
};

ArchivedJobPage.defaultProps = {
  children: null,
  jobTags: [],
};

function mapStateToProps(state) {
  const { job, filter, constant } = state;
  return {
    filter: {
      ...filter.filter.job,
      date: filter.filter.myJobs.date,
    },
    meta: job.archivedJobMeta,
    jobs: job.archivedJobIds.map(id => job.jobs[id]),
    jobTags: constant.constant.jobs.tags,
  };
}

export default connect(mapStateToProps)(translate('')(ArchivedJobPage));
